import React, { Suspense } from "react";
import { lazyRoute } from "./utils/lazyRoute";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { loginReturnPath, saveLoginIntent } from "./utils/loginIntent";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { NetworkProvider, useNetwork } from "./contexts/NetworkContext";
import { Toaster } from "./components/ui/sonner";
import { getSubdomainSlug, resolveRoutedTenantSlug, resolveCustomDomainSlug, isCustomDomainCandidate } from "./utils/tenantResolver";
import ErrorBoundary from "./components/ErrorBoundary";
import NoInternetScreen from "./components/NoInternetScreen";

// Auth Pages
import LoginPage from "./pages/auth/LoginPage";
import StoreSelectPage from "./pages/auth/StoreSelectPage";
import TenantLoginPage from "./pages/auth/TenantLoginPage";

// Customer Pages
import CustomerHome from "./pages/customer/CustomerHome";
import CustomerProducts from "./pages/customer/CustomerProducts";
import CustomerCart from "./pages/customer/CustomerCart";
import CustomerSubscriptions from "./pages/customer/CustomerSubscriptions";
import CustomerOrders from "./pages/customer/CustomerOrders";
import CustomerProfile from "./pages/customer/CustomerProfile";
import CustomerAddresses from "./pages/customer/CustomerAddresses";
import CustomerTracking from "./pages/customer/CustomerTracking";
import ProductDetailPage from "./pages/customer/ProductDetailPage";

import CustomerOrderDetail from "./pages/customer/CustomerOrderDetail";
import NotificationOnboarding from "./components/notifications/NotificationOnboarding";
import { syncCustomerWebPush } from "./utils/webPush";
import { toast } from 'sonner';

/**
 * Lazy import that survives a redeploy.
 *
 * A browser holding a stale index.html will request chunk filenames that no
 * longer exist, and the dynamic import rejects — which would otherwise surface
 * as the generic crash screen on an ordinary navigation. Reload once (guarded by
 * sessionStorage so a genuinely broken chunk can't loop) to pick up the new
 * build; if it fails again, let ErrorBoundary handle it.
 */
// Admin / Super Admin / POS / Delivery-web pages are code-split: none of them are
// reachable in the customer storefront, and bundling them statically forced every
// shopper to download the whole back office before the store could paint.
const AdminDashboard = lazyRoute(() => import("./pages/admin/AdminDashboard"));
const AdminProducts = lazyRoute(() => import("./pages/admin/AdminProducts"));
const AdminCategories = lazyRoute(() => import("./pages/admin/AdminCategories"));
const AdminOrders = lazyRoute(() => import("./pages/admin/AdminOrders"));
const AdminCustomers = lazyRoute(() => import("./pages/admin/AdminCustomers"));
const AdminDeliveryBoys = lazyRoute(() => import("./pages/admin/AdminDeliveryBoys"));
const AdminDeliverySettings = lazyRoute(() => import("./pages/admin/AdminDeliverySettings"));
const AdminCashCollection = lazyRoute(() => import("./pages/admin/AdminCashCollection"));
const AdminRoutes = lazyRoute(() => import("./pages/admin/AdminRoutes"));
const AdminSettings = lazyRoute(() => import("./pages/admin/AdminSettings"));
const AdminBranches = lazyRoute(() => import("./pages/admin/AdminBranches"));
const AdminServiceableApartments = lazyRoute(() => import("./pages/admin/AdminServiceableApartments"));
const AdminAnalytics = lazyRoute(() => import("./pages/admin/AdminAnalytics"));
const AdminBulkUpload = lazyRoute(() => import("./pages/admin/AdminBulkUpload"));
const AdminRouteOptimizer = lazyRoute(() => import("./pages/admin/AdminRouteOptimizer"));
const AdminCoupons = lazyRoute(() => import("./pages/admin/AdminCoupons"));
const AdminPromotions = lazyRoute(() => import("./pages/admin/AdminPromotions"));
const AdminMemberships = lazyRoute(() => import("./pages/admin/AdminMemberships"));
const AdminStoreAnalytics = lazyRoute(() => import("./pages/admin/AdminStoreAnalytics"));
const AdminSocialBanners = lazyRoute(() => import("./pages/admin/AdminSocialBanners"));
const AdminPromoBanners = lazyRoute(() => import("./pages/admin/AdminPromoBanners"));
const AdminSubscriptions = lazyRoute(() => import("./pages/admin/AdminSubscriptions"));
const AdminStock = lazyRoute(() => import("./pages/admin/AdminStock"));
const BranchAvailability = lazyRoute(() => import("./pages/admin/BranchAvailability"));

// Super Admin Pages
const SuperAdminDashboard = lazyRoute(() => import("./pages/superadmin/SuperAdminDashboard"));
const PlatformConfig = lazyRoute(() => import("./pages/superadmin/PlatformConfig"));
const CustomerImport = lazyRoute(() => import("./pages/superadmin/CustomerImport"));

// Delivery Boy Pages (Responsive Web)
const DeliveryDashboard = lazyRoute(() => import("./pages/delivery/DeliveryDashboard"));
const DeliveryOrders = lazyRoute(() => import("./pages/delivery/DeliveryOrders"));
const DeliveryOrderDetail = lazyRoute(() => import("./pages/delivery/DeliveryOrderDetail"));
const DeliveryProfile = lazyRoute(() => import("./pages/delivery/DeliveryProfile"));

// POS Pages
const POSTerminal = lazyRoute(() => import("./pages/pos/POSTerminal"));
const POSInventory = lazyRoute(() => import("./pages/pos/POSInventory"));
const POSVendors = lazyRoute(() => import("./pages/pos/POSVendors"));
const POSPurchases = lazyRoute(() => import("./pages/pos/POSPurchases"));
const POSSalesReport = lazyRoute(() => import("./pages/pos/POSSalesReport"));
const POSSettings = lazyRoute(() => import("./pages/pos/POSSettings"));
const POSShifts = lazyRoute(() => import("./pages/pos/POSShifts"));
const POSBulkUpload = lazyRoute(() => import("./pages/pos/POSBulkUpload"));
const POSDashboard = lazyRoute(() => import("./pages/pos/POSDashboard"));
const POSCustomers = lazyRoute(() => import("./pages/pos/POSCustomers"));

// Legal
const PolicyPage = lazyRoute(() => import("./pages/legal/PolicyPage"));

// The app's single loading visual — shared by the auth gate and by the Suspense
// boundary for code-split routes, so a chunk fetch looks identical to the auth
// check that already precedes these pages rather than introducing a new state.
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[var(--theme-background)]">
    <div className="animate-pulse-soft">
      <div className="w-16 h-16 rounded-full bg-[var(--theme-primary)] opacity-20"></div>
    </div>
  </div>
);

// Protected Route Component
// resumeAfterLogin: a guest sent to /login from this page comes back to it
// after logging in (utils/loginIntent) instead of landing on the home page.
const ProtectedRoute = ({ children, allowedRoles, resumeAfterLogin = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <RouteFallback />;
  }

  if (!user) {
    if (resumeAfterLogin) saveLoginIntent({ returnTo: location.pathname + location.search }, { keepSamePage: true });
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === 'super_admin') return <Navigate to="/super-admin" replace />;
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'branch_admin') return <Navigate to="/admin" replace />;
    if (user.role === 'delivery_boy') return <Navigate to="/delivery" replace />;
    if (user.role === 'customer') {
      if (!user.admin_id) return <Navigate to="/select-store" replace />;
      return <Navigate to="/" replace />;
    }
  }

  if (user.role === 'customer' && !user.admin_id && window.location.pathname !== '/select-store') {
    return <Navigate to="/select-store" replace />;
  }

  return children;
};

// Alert shown in Rider app when user is logged in but not a rider
const NotRiderAlert = () => {
  const { user, logout } = useAuth();
  const navigate = React.useCallback((path) => window.location.href = path, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4" data-testid="not-rider-alert">
      <div className="w-full max-w-md text-center">
        <div className="bg-white rounded-2xl shadow-lg border border-black/5 p-6 space-y-4">
          <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto">
            <svg className="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800">Not Registered as a Rider</h2>
          <p className="text-gray-500 text-sm">
            Your account ({user?.phone}) is not registered as a delivery partner.
            Please contact the store administrator to be added as a rider.
          </p>
          <div className="flex flex-col gap-2 pt-2">
            <button
              onClick={() => { window.location.href = '/'; }}
              className="w-full px-4 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors"
              data-testid="continue-as-customer-btn"
            >
              Continue as Customer
            </button>
            <button
              onClick={() => { logout(); window.location.href = '/rider'; }}
              className="w-full px-4 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
              data-testid="try-different-number-btn"
            >
              Try a Different Number
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Get theme class from store settings
const ThemeWrapper = ({ children }) => {
  const { storeSettings } = useAuth();
  
  const getThemeClass = () => {
    if (!storeSettings?.theme) return '';
    return storeSettings.theme;
  };

  return (
    <div className={getThemeClass()}>
      {children}
    </div>
  );
};

function AppRoutes() {
  const { user, loading } = useAuth();
  const [showNotifOnboarding, setShowNotifOnboarding] = React.useState(false);

  // Web customers: keep an existing browser-notification grant registered to
  // this account. Never prompts — see webPush.js; no-op on native apps, which
  // use FCM via the onboarding below.
  React.useEffect(() => {
    if (user?.role === 'customer' && user?.id) syncCustomerWebPush(user.id);
  }, [user?.id, user?.role]);

  // Check if we should show notification onboarding after login
  React.useEffect(() => {
    if (user && !loading) {
      import('./utils/pushService').then(({ shouldShowOnboarding, syncTokenToBackend }) => {
        if (shouldShowOnboarding()) {
          // Small delay so user sees the home screen first
          const timer = setTimeout(() => setShowNotifOnboarding(true), 1500);
          return () => clearTimeout(timer);
        }
        // If already granted, sync token on login
        syncTokenToBackend();
      });
    }
  }, [user, loading]);

  // Listen for foreground push notifications — only on Android
  // On iOS, presentationOptions ["badge","sound","alert"] already shows the native notification
  React.useEffect(() => {
    const handler = (e) => {
      const n = e.detail;
      if (n?.title) {
        const platform = window.Capacitor?.getPlatform?.();
        if (platform !== 'ios') {
          toast.info(n.title, { description: n.body, duration: 5000 });
        }
      }
    };
    document.addEventListener('push-notification', handler);
    return () => document.removeEventListener('push-notification', handler);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--theme-background)]">
        <div className="animate-pulse-soft">
          <div className="w-16 h-16 rounded-full bg-[var(--theme-primary)] opacity-20"></div>
        </div>
      </div>
    );
  }

  return (
    <ThemeWrapper>
      {/* Notification Permission Onboarding (native only, after first login) */}
      {showNotifOnboarding && (
        <NotificationOnboarding
          onComplete={() => setShowNotifOnboarding(false)}
          userRole={user?.role === 'delivery_boy' ? 'delivery' : 'customer'}
        />
      )}
      {/* One boundary around the whole table: only code-split routes suspend, and
          each already sits behind the identical auth-gate spinner, so eager
          customer routes are unaffected. */}
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Auth Routes */}
        <Route path="/login" element={
          user
            ? (user.role === 'delivery_boy' ? <Navigate to="/delivery" replace /> :
               user.role === 'super_admin' ? <Navigate to="/super-admin" replace /> :
               user.role === 'admin' || user.role === 'branch_admin' ? <Navigate to="/admin" replace /> :
               <Navigate to={(user.role === 'customer' && loginReturnPath()) || '/'} replace />)
            : (getSubdomainSlug() ? <TenantLoginPage /> : <LoginPage />)
        } />
        <Route path="/select-store" element={
          <ProtectedRoute allowedRoles={['customer']}>
            <StoreSelectPage />
          </ProtectedRoute>
        } />

        {/* Tenant-Specific Login */}
        <Route path="/t/:slug" element={<TenantLoginPage />} />

        {/* Rider App Entry — /rider path loads tenant login locked to delivery role */}
        <Route path="/rider" element={
          user
            ? (user.role === 'delivery_boy' ? <Navigate to="/delivery" replace /> :
               user.role === 'admin' || user.role === 'branch_admin' ? <Navigate to="/admin" replace /> :
               <NotRiderAlert />)
            : (getSubdomainSlug() ? <TenantLoginPage riderMode /> : <LoginPage riderMode />)
        } />

        {/* Legal Pages (public, no auth — permanent URLs for App Store) */}
        <Route path="/legal/:slug/:app/:kind" element={<PolicyPage />} />
        <Route path="/legal/:slug/:kind" element={<PolicyPage />} />

        {/* Customer Routes — Home & Products accessible without login */}
        <Route path="/" element={
          user ? (
            user.role === 'customer' ? <CustomerHome /> :
            user.role === 'admin' || user.role === 'branch_admin' ? <Navigate to="/admin" replace /> :
            user.role === 'delivery_boy' ? <Navigate to="/delivery" replace /> :
            user.role === 'super_admin' ? <Navigate to="/super-admin" replace /> :
            <CustomerHome />
          ) : <CustomerHome />
        } />
        <Route path="/products" element={
          user && user.role !== 'customer' ? <Navigate to="/" replace /> : <CustomerProducts />
        } />
        <Route path="/product/:productId" element={<ProductDetailPage />} />
        <Route path="/cart" element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CustomerCart />
          </ProtectedRoute>
        } />
        <Route path="/subscriptions" element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CustomerSubscriptions />
          </ProtectedRoute>
        } />
        <Route path="/orders" element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CustomerOrders />
          </ProtectedRoute>
        } />
        <Route path="/orders/:orderId" element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CustomerOrderDetail />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CustomerProfile />
          </ProtectedRoute>
        } />
        <Route path="/customer/addresses" element={
          <ProtectedRoute allowedRoles={['customer']} resumeAfterLogin>
            <CustomerAddresses />
          </ProtectedRoute>
        } />
        <Route path="/tracking" element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CustomerTracking />
          </ProtectedRoute>
        } />

        {/* Admin Routes (admin + branch_admin) */}
        <Route path="/admin" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/products" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <AdminProducts />
          </ProtectedRoute>
        } />
        <Route path="/admin/categories" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <AdminCategories />
          </ProtectedRoute>
        } />
        <Route path="/admin/orders" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <AdminOrders />
          </ProtectedRoute>
        } />
        <Route path="/admin/customers" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <AdminCustomers />
          </ProtectedRoute>
        } />
        <Route path="/admin/delivery-boys" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <AdminDeliveryBoys />
          </ProtectedRoute>
        } />
        <Route path="/admin/delivery-settings" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDeliverySettings />
          </ProtectedRoute>
        } />
        <Route path="/admin/cash-collection" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminCashCollection />
          </ProtectedRoute>
        } />
        <Route path="/admin/routes" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminRoutes />
          </ProtectedRoute>
        } />
        <Route path="/admin/branches" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminBranches />
          </ProtectedRoute>
        } />
        <Route path="/admin/serviceable-apartments" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <AdminServiceableApartments />
          </ProtectedRoute>
        } />
        <Route path="/admin/analytics" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <AdminAnalytics />
          </ProtectedRoute>
        } />
        <Route path="/admin/bulk-upload" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <AdminBulkUpload />
          </ProtectedRoute>
        } />
        <Route path="/admin/route-optimizer" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <AdminRouteOptimizer />
          </ProtectedRoute>
        } />
        <Route path="/admin/coupons" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminCoupons />
          </ProtectedRoute>
        } />
        <Route path="/admin/promotions" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPromotions />
          </ProtectedRoute>
        } />
        <Route path="/admin/memberships" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminMemberships />
          </ProtectedRoute>
        } />
        <Route path="/admin/settings" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminSettings />
          </ProtectedRoute>
        } />
        <Route path="/admin/stock" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminStock />
          </ProtectedRoute>
        } />
        <Route path="/admin/subscriptions" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <AdminSubscriptions />
          </ProtectedRoute>
        } />
        <Route path="/admin/store-analytics" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <AdminStoreAnalytics />
          </ProtectedRoute>
        } />
        <Route path="/admin/social-banners" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminSocialBanners />
          </ProtectedRoute>
        } />
        <Route path="/admin/promo-banners" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPromoBanners />
          </ProtectedRoute>
        } />
        <Route path="/admin/branches/:branchId/availability" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <BranchAvailability />
          </ProtectedRoute>
        } />

        {/* Super Admin Routes */}
        <Route path="/super-admin" element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/super-admin/config" element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <PlatformConfig />
          </ProtectedRoute>
        } />
        <Route path="/super-admin/import-customers" element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <CustomerImport />
          </ProtectedRoute>
        } />

        {/* Delivery Boy Routes (Responsive Web) */}
        <Route path="/delivery" element={
          <ProtectedRoute allowedRoles={['delivery_boy']}>
            <DeliveryDashboard />
          </ProtectedRoute>
        } />
        <Route path="/delivery/orders" element={
          <ProtectedRoute allowedRoles={['delivery_boy']}>
            <DeliveryOrders />
          </ProtectedRoute>
        } />
        <Route path="/delivery/orders/:orderId" element={
          <ProtectedRoute allowedRoles={['delivery_boy']}>
            <DeliveryOrderDetail />
          </ProtectedRoute>
        } />
        <Route path="/delivery/profile" element={
          <ProtectedRoute allowedRoles={['delivery_boy']}>
            <DeliveryProfile />
          </ProtectedRoute>
        } />

        {/* POS Routes */}
        <Route path="/pos" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <POSTerminal />
          </ProtectedRoute>
        } />
        <Route path="/pos/dashboard" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <POSDashboard />
          </ProtectedRoute>
        } />
        <Route path="/pos/inventory" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <POSInventory />
          </ProtectedRoute>
        } />
        <Route path="/pos/vendors" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <POSVendors />
          </ProtectedRoute>
        } />
        <Route path="/pos/purchases" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <POSPurchases />
          </ProtectedRoute>
        } />
        <Route path="/pos/reports" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <POSSalesReport />
          </ProtectedRoute>
        } />
        <Route path="/pos/shifts" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <POSShifts />
          </ProtectedRoute>
        } />
        <Route path="/pos/bulk-upload" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <POSBulkUpload />
          </ProtectedRoute>
        } />
        <Route path="/pos/settings" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <POSSettings />
          </ProtectedRoute>
        } />
        <Route path="/pos/customers" element={
          <ProtectedRoute allowedRoles={['admin', 'branch_admin']}>
            <POSCustomers />
          </ProtectedRoute>
        } />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </ThemeWrapper>
  );
}

// Gates the authenticated app tree on connectivity. Rendering NoInternetScreen
// INSTEAD of AuthProvider/CartProvider — rather than as an overlay on top — means
// nothing below can issue an API call while offline, and reconnecting simply
// mounts the tree fresh: AuthContext's loadUser, CartContext's localStorage
// rehydration, and every page's own on-mount data fetch all just happen again
// naturally. No per-page retry logic needed.
const NetworkGate = () => {
  const { isOnline } = useNetwork();

  if (!isOnline) {
    return <NoInternetScreen />;
  }

  return (
    <AuthProvider>
      <CartProvider>
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </CartProvider>
    </AuthProvider>
  );
};

// Resolves a tenant's custom domain (e.g. arfreshmart.in / www.arfreshmart.in)
// before anything else mounts, so AuthProvider's synchronous resolveTenantSlug()
// already finds the slug cached. Renders immediately, with no network call, for
// every case that isn't a genuine unresolved custom domain — including when a
// build-time REACT_APP_DEFAULT_TENANT_SLUG is set, since that default must not
// pre-empt the real per-domain lookup for a tenant's own custom domain.
const TenantGate = ({ children }) => {
  const [ready, setReady] = React.useState(() => !!resolveRoutedTenantSlug() || !isCustomDomainCandidate());

  React.useEffect(() => {
    if (ready) return;
    let cancelled = false;
    resolveCustomDomainSlug().finally(() => {
      if (!cancelled) setReady(true);
    });
    return () => { cancelled = true; };
  }, [ready]);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-pulse-soft">
          <div className="w-16 h-16 rounded-full bg-gray-200"></div>
        </div>
      </div>
    );
  }

  return children;
};

function App() {
  React.useEffect(() => {
    // Signal to native bridge that React is mounted
    import('./utils/nativeBridge').then(({ onAppReady }) => onAppReady());
  }, []);

  return (
    <ErrorBoundary>
      <TenantGate>
        <BrowserRouter>
          <NetworkProvider>
            <NetworkGate />
          </NetworkProvider>
        </BrowserRouter>
      </TenantGate>
    </ErrorBoundary>
  );
}

export default App;
